import { fnv1a32RecordHash } from "./provenance.mjs";

function byId(items, key = "id") {
  return new Map(items.map((item) => [item[key], item]));
}

function sourceSummaries(sourceIds, sourceRegistryById) {
  return sourceIds
    .map((sourceId) => sourceRegistryById.get(sourceId))
    .filter(Boolean)
    .map((source) => ({
      sourceId: source.sourceId,
      title: source.title,
      trust: source.trust,
      url: source.url,
      recordHash: source.recordHash,
      uncertaintyMode: source.uncertaintyMode,
    }));
}

function topItems(items, count = 5) {
  return [...(items ?? [])].slice(0, count);
}

export function buildFeasibilityReport({
  build,
  generatedAt,
  selectedScenarioId,
  inputs,
  rankedScenarios,
  uncertaintyAnalysis,
  businessCases,
  frontier,
  sourceRegistryV2,
  claims,
  modelArtifacts,
  generatedAsteroidCandidateEvidenceLedger,
  generatedCandidateSourceClosurePackets,
  generatedCandidateSourcePromotionLedger,
  generatedClaimEvidenceLedger,
  generatedModelArtifactBundle,
  generatedMaterialsProcessEvidenceLedger,
  generatedSpaceEnvironmentEvidenceLedger,
  generatedModelValidationProtocolLedger,
  generatedModelRunLedger,
  generatedModelRunComparisonLedger,
  generatedEconomicsFrontierReport,
  generatedEconomicsEvidenceLedger,
  generatedAssumptionImpactLedger,
  generatedCapabilityMaturityLedger,
  generatedCustomerDecisionDossierLedger,
  generatedScenarioDecisionLedger,
  generatedScenarioDecisionAuditLedger,
  generatedScenarioCrossoverFrontierLedger,
  generatedScenarioUncertaintyBudgetLedger,
  generatedSidecarValidationReport,
  generatedSidecarScaffoldRejectionLedger,
  generatedSidecarWorkOrderPacket,
  generatedTrajectoryResidualAcceptanceLedger,
  generatedTrajectoryResidualEvidenceLedger,
  generatedSourceIngestionLedger,
  generatedSourceUseClearanceDossier,
  generatedSourceUseReviewPacket,
  generatedSourceUseRightsLedger,
  generatedWorkspaceLedger,
  assumptionRegistry,
  scenarioNotebooks,
  readinessCategories,
}) {
  const sourceRegistryById = byId(sourceRegistryV2, "sourceId");
  const scenarioById = byId(rankedScenarios);
  const selectedScenario = scenarioById.get(selectedScenarioId) ?? rankedScenarios[0];
  const selectedUncertainty =
    uncertaintyAnalysis.summaries.find((summary) => summary.scenarioId === selectedScenario.id) ??
    uncertaintyAnalysis.summaries[0];
  const selectedBusinessCase =
    businessCases.find((entry) => entry.scenarioId === selectedScenario.id) ?? businessCases[0];
  const verifiedClaims = claims.filter((claim) => claim.status === "Verified");
  const highImpactUnverified = claims.filter(
    (claim) => claim.impact === "High" && claim.status !== "Verified"
  );
  const blockingReadiness = readinessCategories.filter(
    (category) => category.currentScore < category.targetScore
  );
  const selectedNotebook = scenarioNotebooks.find((notebook) =>
    notebook.title.toLowerCase().includes(selectedScenario.name.split(" ")[0].toLowerCase())
  );
  const selectedDecision =
    generatedScenarioDecisionLedger?.decisions.find(
      (decision) => decision.scenarioId === selectedScenario.id
    ) ?? null;
  const nearestCrossover = generatedScenarioCrossoverFrontierLedger?.nearestCrossover ?? null;
  const decisionAudit =
    generatedScenarioDecisionAuditLedger?.audits.find(
      (audit) => audit.scenarioId === selectedScenario.id
    ) ?? null;
  const selectedUncertaintyBudget =
    generatedScenarioUncertaintyBudgetLedger?.budgets.find(
      (budget) => budget.scenarioId === selectedScenario.id
    ) ?? null;
  const topEvidenceGaps = [
    ...topItems(generatedSourceUseReviewPacket?.reviewQueue, 2).map((packet) => ({
      id: packet.id,
      lane: "source-use",
      title: packet.title,
      state: "blocked-customer-use-review",
      nextAction: packet.nextAction,
    })),
    ...topItems(generatedSidecarWorkOrderPacket?.workOrders, 2).map((order) => ({
      id: order.id,
      lane: "runtime-sidecar",
      title: `${order.tool} ${order.contractId}`,
      state: order.executionState,
      nextAction: order.commandTemplate,
    })),
    ...topItems(generatedEconomicsEvidenceLedger?.records, 1).map((record) => ({
      id: record.id,
      lane: "economics-source",
      title: record.label,
      state: record.promotionGate?.state ?? record.dataState,
      nextAction: record.promotionGate?.blockers?.[0] ?? record.currentUse,
    })),
  ];
  const decisionMemoCore = {
    mode: "business-feasibility-decision-memo",
    customerUseState:
      generatedSourceUseRightsLedger?.customerUseAllowed === true &&
      generatedSourceUseReviewPacket?.customerUseAllowed === true
        ? "reviewable-for-customer-delivery"
        : "blocked-until-source-use-review",
    sellableBetaLane: "paid-beta-internal-diligence-with-citation-review",
    selectedScenarioId: selectedScenario.id,
    selectedScenarioName: selectedScenario.name,
    topDecisionScenarioId: generatedScenarioDecisionLedger?.topDecision?.scenarioId ?? null,
    topDecisionLane: generatedScenarioDecisionLedger?.topDecision?.decisionLane ?? null,
    selectedDecisionLane: selectedDecision?.decisionLane ?? null,
    selectedDecisionReadinessScore: selectedDecision?.decisionReadinessScore ?? null,
    commercializationAllowed: false,
    confidenceBand:
      selectedDecision?.decisionReadinessScore >= 74
        ? "medium-beta-confidence"
        : selectedDecision?.decisionReadinessScore >= 62
          ? "conditional-beta-confidence"
          : "research-backlog-confidence",
    investmentQuestion:
      "Which off-world industrial scenario deserves the next paid diligence sprint, what assumptions drive that answer, and what evidence would change it?",
    currentAnswer:
      "Use the current ranking as decision support only; it is not a mission design, verified resource claim, or cleared customer report until the listed gates close.",
    buyerFacingValue:
      "Converts public science, engineering, economics, and source-rights evidence into reproducible scenario comparisons with explicit uncertainty and blockers.",
    proofToChangeAnswer: [
      nearestCrossover
        ? {
            lane: "scenario-crossover",
            requiredChange: nearestCrossover.requiredUtilityDelta,
            challengerScenarioId: nearestCrossover.challengerScenarioId,
            state: nearestCrossover.crossoverState,
          }
        : null,
      decisionAudit
        ? {
            lane: "decision-audit",
            scenarioId: decisionAudit.scenarioId,
            proofBurden: decisionAudit.proofBurden,
            flipCondition: decisionAudit.flipCondition,
          }
        : null,
    ].filter(Boolean),
    topBlockers: topItems(
      [
        ...(selectedDecision?.blockers ?? []),
        ...(generatedSourceUseRightsLedger?.customerUseAllowed
          ? []
          : ["Customer-facing report use is blocked until source-use review packets are approved."]),
        ...(generatedSidecarValidationReport?.blockedCount > 0
          ? [
              `${generatedSidecarValidationReport.blockedCount} science sidecar validation artifact(s) remain blocked.`,
            ]
          : []),
        ...(generatedEconomicsEvidenceLedger?.promotionAllowed
          ? []
          : ["Economics claims remain planning heuristics until dated cost/capex/opex source records promote."]),
        ...(generatedCandidateSourcePromotionLedger?.resourcePromotionAllowed &&
        generatedCandidateSourcePromotionLedger?.customerUseAllowed
          ? []
          : [
              "Candidate asteroid/feedstock conclusions are blocked until candidate-specific source custody, resource evidence, and customer-use gates promote.",
            ]),
      ],
      8
    ),
    nextProofActions: topEvidenceGaps,
    evidenceHashes: {
      scenarioDecisionLedger: generatedScenarioDecisionLedger?.ledgerHash ?? null,
      customerDecisionDossierLedger:
        generatedCustomerDecisionDossierLedger?.ledgerHash ?? null,
      modelRunComparisonLedger: generatedModelRunComparisonLedger?.ledgerHash ?? null,
      scenarioDecisionAuditLedger: generatedScenarioDecisionAuditLedger?.ledgerHash ?? null,
      scenarioCrossoverFrontier: generatedScenarioCrossoverFrontierLedger?.ledgerHash ?? null,
      scenarioUncertaintyBudgetLedger: generatedScenarioUncertaintyBudgetLedger?.ledgerHash ?? null,
      economicsEvidenceLedger: generatedEconomicsEvidenceLedger?.ledgerHash ?? null,
      sourceUseRightsLedger: generatedSourceUseRightsLedger?.ledgerHash ?? null,
      sourceUseReviewPacket: generatedSourceUseReviewPacket?.packetHash ?? null,
      sourceUseClearanceDossier: generatedSourceUseClearanceDossier?.dossierHash ?? null,
      sidecarValidationReport: generatedSidecarValidationReport?.reportHash ?? null,
      sidecarWorkOrderPacket: generatedSidecarWorkOrderPacket?.packetHash ?? null,
      trajectoryResidualAcceptanceLedger:
        generatedTrajectoryResidualAcceptanceLedger?.ledgerHash ?? null,
      trajectoryResidualEvidenceLedger:
        generatedTrajectoryResidualEvidenceLedger?.ledgerHash ?? null,
      candidateSourceClosurePackets:
        generatedCandidateSourceClosurePackets?.packetHash ?? null,
      candidateSourcePromotionLedger:
        generatedCandidateSourcePromotionLedger?.ledgerHash ?? null,
    },
  };
  const decisionMemo = {
    ...decisionMemoCore,
    memoHash: fnv1a32RecordHash(decisionMemoCore),
  };

  const reportCore = {
    schemaVersion: 1,
    title: "Interplanetary Industrial Feasibility Report",
    mode: "decision-support-report-not-operational-mission-design",
    generatedAt,
    build,
    inputs,
    executiveSummary: {
      selectedScenarioId: selectedScenario.id,
      selectedScenarioName: selectedScenario.name,
      deterministicUtility: selectedScenario.scores.utility,
      medianUtility: selectedUncertainty.utilityMedian,
      utilityRangeP10P90: [selectedUncertainty.utilityP10, selectedUncertainty.utilityP90],
      winProbabilityPercent: selectedUncertainty.winProbability,
      businessReadiness: selectedBusinessCase.businessReadiness,
      totalProgramNeedBn: selectedBusinessCase.totalProgramNeedBn,
      capitalGapBn: selectedBusinessCase.capitalGapBn,
      fundingCoveragePercent: selectedBusinessCase.fundingCoverage,
      recommendationCaveat:
        "Planner outputs are heuristics until dated cost sources, runtime trajectory artifacts, and source-backed feedstock evidence are attached.",
    },
    scenarioComparison: rankedScenarios.map((scenario) => {
      const uncertainty = uncertaintyAnalysis.summaries.find(
        (summary) => summary.scenarioId === scenario.id
      );
      const businessCase = businessCases.find((entry) => entry.scenarioId === scenario.id);
      return {
        scenarioId: scenario.id,
        name: scenario.name,
        summary: scenario.summary,
        utility: scenario.scores.utility,
        benefitScore: scenario.scores.benefitScore,
        riskScore: scenario.scores.riskScore,
        uncertaintyPenalty: scenario.uncertaintyPenalty,
        medianUtility: uncertainty?.utilityMedian ?? null,
        winProbabilityPercent: uncertainty?.winProbability ?? null,
        businessReadiness: businessCase?.businessReadiness ?? null,
        totalProgramNeedBn: businessCase?.totalProgramNeedBn ?? null,
        capitalGapBn: businessCase?.capitalGapBn ?? null,
      };
    }),
    paretoFrontier: frontier.map((scenario) => ({
      scenarioId: scenario.id,
      name: scenario.name,
      utility: scenario.scores.utility,
      uncertaintyPenalty: scenario.uncertaintyPenalty,
    })),
    decisionMemo,
    evidence: {
      sourceRegistryHash: generatedModelArtifactBundle.sourceRegistryHash,
      asteroidCandidateEvidenceLedgerHash:
        generatedAsteroidCandidateEvidenceLedger?.ledgerHash ?? null,
      candidateSourceClosurePacketHash:
        generatedCandidateSourceClosurePackets?.packetHash ?? null,
      candidateSourcePromotionLedgerHash:
        generatedCandidateSourcePromotionLedger?.ledgerHash ?? null,
      claimEvidenceLedgerHash: generatedClaimEvidenceLedger?.ledgerHash ?? null,
      modelArtifactBundleHash: generatedModelArtifactBundle.bundleHash,
      materialsProcessEvidenceLedgerHash:
        generatedMaterialsProcessEvidenceLedger?.ledgerHash ?? null,
      spaceEnvironmentEvidenceLedgerHash:
        generatedSpaceEnvironmentEvidenceLedger?.ledgerHash ?? null,
      modelValidationProtocolLedgerHash:
        generatedModelValidationProtocolLedger?.ledgerHash ?? null,
      modelRunLedgerHash: generatedModelRunLedger?.ledgerHash ?? null,
      modelRunComparisonLedgerHash: generatedModelRunComparisonLedger?.ledgerHash ?? null,
      economicsFrontierHash: generatedEconomicsFrontierReport?.reportHash ?? null,
      economicsEvidenceLedgerHash: generatedEconomicsEvidenceLedger?.ledgerHash ?? null,
      assumptionImpactLedgerHash: generatedAssumptionImpactLedger?.ledgerHash ?? null,
      capabilityMaturityLedgerHash: generatedCapabilityMaturityLedger?.ledgerHash ?? null,
      customerDecisionDossierLedgerHash:
        generatedCustomerDecisionDossierLedger?.ledgerHash ?? null,
      scenarioDecisionLedgerHash: generatedScenarioDecisionLedger?.ledgerHash ?? null,
      scenarioCrossoverFrontierHash:
        generatedScenarioCrossoverFrontierLedger?.ledgerHash ?? null,
      scenarioUncertaintyBudgetLedgerHash:
        generatedScenarioUncertaintyBudgetLedger?.ledgerHash ?? null,
      scenarioDecisionAuditLedgerHash:
        generatedScenarioDecisionAuditLedger?.ledgerHash ?? null,
      trajectoryResidualAcceptanceLedgerHash:
        generatedTrajectoryResidualAcceptanceLedger?.ledgerHash ?? null,
      trajectoryResidualEvidenceLedgerHash:
        generatedTrajectoryResidualEvidenceLedger?.ledgerHash ?? null,
      economicsFrontier: generatedEconomicsFrontierReport
        ? {
            mode: generatedEconomicsFrontierReport.mode,
            artifactId: generatedEconomicsFrontierReport.artifactId,
            claimState: generatedEconomicsFrontierReport.claimState,
            iterations: generatedEconomicsFrontierReport.iterations,
            seed: generatedEconomicsFrontierReport.seed,
            reportHash: generatedEconomicsFrontierReport.reportHash,
            mostLikelyBusinessWinner:
              generatedEconomicsFrontierReport.mostLikelyBusinessWinner?.scenarioId ?? null,
            promotionAllowed: generatedEconomicsFrontierReport.promotionAllowed,
            blockerCount: generatedEconomicsFrontierReport.promotionBlockers?.length ?? 0,
          }
        : null,
      workspaceLedgerHash: generatedWorkspaceLedger?.ledgerHash ?? null,
      modelRuns: generatedModelRunLedger
        ? {
            mode: generatedModelRunLedger.mode,
            runCount: generatedModelRunLedger.runCount,
            ledgerHash: generatedModelRunLedger.ledgerHash,
            selectedRun:
              generatedModelRunLedger.runs.find(
                (run) => run.selectedScenario.scenarioId === selectedScenario.id
              ) ?? generatedModelRunLedger.runs[0],
          }
        : null,
      modelRunComparisons: generatedModelRunComparisonLedger
        ? {
            mode: generatedModelRunComparisonLedger.mode,
            comparisonCount: generatedModelRunComparisonLedger.comparisonCount,
            selectedScenarioChangeCount:
              generatedModelRunComparisonLedger.selectedScenarioChangeCount,
            rankOneChangeCount: generatedModelRunComparisonLedger.rankOneChangeCount,
            commercialUseAllowed:
              generatedModelRunComparisonLedger.commercialUseAllowed,
            ledgerHash: generatedModelRunComparisonLedger.ledgerHash,
            comparisons: generatedModelRunComparisonLedger.comparisons.map((comparison) => ({
              id: comparison.id,
              baselineRunId: comparison.baselineRun.id,
              comparisonRunId: comparison.comparisonRun.id,
              selectedScenarioChanged: comparison.selectedScenarioChanged,
              rankOneChanged: comparison.rankOneChanged,
              biggestRankMover: comparison.biggestRankMover,
              biggestUtilityMover: comparison.biggestUtilityMover,
              comparisonHash: comparison.comparisonHash,
            })),
          }
        : null,
      customerDecisionDossiers: generatedCustomerDecisionDossierLedger
        ? {
            mode: generatedCustomerDecisionDossierLedger.mode,
            scenarioCount: generatedCustomerDecisionDossierLedger.scenarioCount,
            paidBetaDossierCount:
              generatedCustomerDecisionDossierLedger.paidBetaDossierCount,
            blockedDossierCount:
              generatedCustomerDecisionDossierLedger.blockedDossierCount,
            customerReviewReadyCount:
              generatedCustomerDecisionDossierLedger.customerReviewReadyCount,
            customerUseAllowed: generatedCustomerDecisionDossierLedger.customerUseAllowed,
            commercialUseAllowed:
              generatedCustomerDecisionDossierLedger.commercialUseAllowed,
            ledgerHash: generatedCustomerDecisionDossierLedger.ledgerHash,
            selectedScenarioDossier:
              generatedCustomerDecisionDossierLedger.dossiers.find(
                (dossier) => dossier.scenarioId === selectedScenario.id
              ) ?? null,
          }
        : null,
      economicsEvidenceLedger: generatedEconomicsEvidenceLedger
        ? {
            mode: generatedEconomicsEvidenceLedger.mode,
            recordCount: generatedEconomicsEvidenceLedger.recordCount,
            blockedRecordCount: generatedEconomicsEvidenceLedger.blockedRecordCount,
            promotionAllowed: generatedEconomicsEvidenceLedger.promotionAllowed,
            ledgerHash: generatedEconomicsEvidenceLedger.ledgerHash,
            linkedAssumptions: generatedEconomicsEvidenceLedger.linkedAssumptions.map(
              (assumption) => ({
                id: assumption.id,
                state: assumption.state,
                uncertainty: assumption.uncertainty,
              })
            ),
          }
        : null,
      materialsProcessEvidenceLedger: generatedMaterialsProcessEvidenceLedger
        ? {
            mode: generatedMaterialsProcessEvidenceLedger.mode,
            processCount: generatedMaterialsProcessEvidenceLedger.processCount,
            blockedProcessCount:
              generatedMaterialsProcessEvidenceLedger.blockedProcessCount,
            promotionAllowed: generatedMaterialsProcessEvidenceLedger.promotionAllowed,
            ledgerHash: generatedMaterialsProcessEvidenceLedger.ledgerHash,
            topProcessGaps: generatedMaterialsProcessEvidenceLedger.topProcessGaps,
          }
        : null,
      spaceEnvironmentEvidenceLedger: generatedSpaceEnvironmentEvidenceLedger
        ? {
            mode: generatedSpaceEnvironmentEvidenceLedger.mode,
            recordCount: generatedSpaceEnvironmentEvidenceLedger.recordCount,
            blockedRecordCount:
              generatedSpaceEnvironmentEvidenceLedger.blockedRecordCount,
            promotionAllowed: generatedSpaceEnvironmentEvidenceLedger.promotionAllowed,
            ledgerHash: generatedSpaceEnvironmentEvidenceLedger.ledgerHash,
            topEnvironmentGaps:
              generatedSpaceEnvironmentEvidenceLedger.topEnvironmentGaps,
            capabilityContext:
              generatedSpaceEnvironmentEvidenceLedger.capabilityContext,
          }
        : null,
      asteroidCandidateEvidenceLedger: generatedAsteroidCandidateEvidenceLedger
        ? {
            mode: generatedAsteroidCandidateEvidenceLedger.mode,
            candidateCount: generatedAsteroidCandidateEvidenceLedger.candidateCount,
            blockedCandidateCount:
              generatedAsteroidCandidateEvidenceLedger.blockedCandidateCount,
            promotionAllowed: generatedAsteroidCandidateEvidenceLedger.promotionAllowed,
            ledgerHash: generatedAsteroidCandidateEvidenceLedger.ledgerHash,
            globalBlockers: generatedAsteroidCandidateEvidenceLedger.globalBlockers,
          }
        : null,
      candidateSourcePromotionLedger: generatedCandidateSourcePromotionLedger
        ? {
            mode: generatedCandidateSourcePromotionLedger.mode,
            reportCount: generatedCandidateSourcePromotionLedger.reportCount,
            validReportCount: generatedCandidateSourcePromotionLedger.validReportCount,
            sourceCustodyPromotedCount:
              generatedCandidateSourcePromotionLedger.sourceCustodyPromotedCount,
            resourcePromotedCount:
              generatedCandidateSourcePromotionLedger.resourcePromotedCount,
            customerUseAllowedCount:
              generatedCandidateSourcePromotionLedger.customerUseAllowedCount,
            sourceCustodyPromotionAllowed:
              generatedCandidateSourcePromotionLedger.sourceCustodyPromotionAllowed,
            resourcePromotionAllowed:
              generatedCandidateSourcePromotionLedger.resourcePromotionAllowed,
            customerUseAllowed: generatedCandidateSourcePromotionLedger.customerUseAllowed,
            promotionState: generatedCandidateSourcePromotionLedger.promotionState,
            workOrderGapCount:
              generatedCandidateSourcePromotionLedger.workOrderGaps?.length ?? 0,
            ledgerHash: generatedCandidateSourcePromotionLedger.ledgerHash,
            topWorkOrderGaps:
              generatedCandidateSourcePromotionLedger.workOrderGaps?.slice(0, 5) ?? [],
          }
        : null,
      candidateSourceClosurePackets: generatedCandidateSourceClosurePackets
        ? {
            mode: generatedCandidateSourceClosurePackets.mode,
            packetCount: generatedCandidateSourceClosurePackets.packetCount,
            draftIntakePresentCount:
              generatedCandidateSourceClosurePackets.draftIntakePresentCount,
            missingIntakeArtifactCount:
              generatedCandidateSourceClosurePackets.missingIntakeArtifactCount,
            sourceCustodyBlockedCount:
              generatedCandidateSourceClosurePackets.sourceCustodyBlockedCount,
            resourcePromotionBlockedCount:
              generatedCandidateSourceClosurePackets.resourcePromotionBlockedCount,
            customerUseBlockedCount:
              generatedCandidateSourceClosurePackets.customerUseBlockedCount,
            packetHash: generatedCandidateSourceClosurePackets.packetHash,
            topPackets:
              generatedCandidateSourceClosurePackets.packets?.slice(0, 3).map((packet) => ({
                id: packet.id,
                designation: packet.designation,
                intakeState: packet.intakeState,
                requiredClosureItemCount: packet.requiredClosureItems.length,
                packetHash: packet.packetHash,
              })) ?? [],
          }
        : null,
      trajectoryResidualAcceptanceLedger: generatedTrajectoryResidualAcceptanceLedger
        ? {
            mode: generatedTrajectoryResidualAcceptanceLedger.mode,
            residualAcceptanceCount:
              generatedTrajectoryResidualAcceptanceLedger.residualAcceptanceCount,
            blockedResidualAcceptanceCount:
              generatedTrajectoryResidualAcceptanceLedger.blockedResidualAcceptanceCount,
            decisionUseAllowed:
              generatedTrajectoryResidualAcceptanceLedger.decisionUseAllowed,
            customerUseAllowed:
              generatedTrajectoryResidualAcceptanceLedger.customerUseAllowed,
            commercialUseAllowed:
              generatedTrajectoryResidualAcceptanceLedger.commercialUseAllowed,
            ledgerHash: generatedTrajectoryResidualAcceptanceLedger.ledgerHash,
            topAcceptanceRecords:
              generatedTrajectoryResidualAcceptanceLedger.acceptanceRecords
                ?.slice(0, 3)
                .map((record) => ({
                  id: record.id,
                  currentState: record.currentState,
                  comparisonType: record.comparisonType,
                  blockerCount: record.blockers.length,
                  acceptanceHash: record.acceptanceHash,
                })) ?? [],
          }
        : null,
      trajectoryResidualEvidenceLedger: generatedTrajectoryResidualEvidenceLedger
        ? {
            mode: generatedTrajectoryResidualEvidenceLedger.mode,
            acceptanceLaneCount:
              generatedTrajectoryResidualEvidenceLedger.acceptanceLaneCount,
            reportCount: generatedTrajectoryResidualEvidenceLedger.reportCount,
            promotedResidualEvidenceCount:
              generatedTrajectoryResidualEvidenceLedger.promotedResidualEvidenceCount,
            blockedLaneCount: generatedTrajectoryResidualEvidenceLedger.blockedLaneCount,
            promotionAllowed: generatedTrajectoryResidualEvidenceLedger.promotionAllowed,
            decisionUseAllowed:
              generatedTrajectoryResidualEvidenceLedger.decisionUseAllowed,
            customerUseAllowed:
              generatedTrajectoryResidualEvidenceLedger.customerUseAllowed,
            commercialUseAllowed:
              generatedTrajectoryResidualEvidenceLedger.commercialUseAllowed,
            ledgerHash: generatedTrajectoryResidualEvidenceLedger.ledgerHash,
            topLaneRecords:
              generatedTrajectoryResidualEvidenceLedger.laneRecords
                ?.slice(0, 3)
                .map((record) => ({
                  id: record.acceptanceLaneId,
                  evidenceState: record.evidenceState,
                  blockerCount: record.blockerCount,
                  laneEvidenceHash: record.laneEvidenceHash,
                })) ?? [],
          }
        : null,
      assumptionImpactLedger: generatedAssumptionImpactLedger
        ? {
            mode: generatedAssumptionImpactLedger.mode,
            assumptionCount: generatedAssumptionImpactLedger.assumptionCount,
            blockedAssumptionCount: generatedAssumptionImpactLedger.blockedAssumptionCount,
            ledgerHash: generatedAssumptionImpactLedger.ledgerHash,
            mostSensitiveAssumptions:
              generatedAssumptionImpactLedger.mostSensitiveAssumptions,
          }
        : null,
      scenarioDecisionLedger: generatedScenarioDecisionLedger
        ? {
            mode: generatedScenarioDecisionLedger.mode,
            scenarioCount: generatedScenarioDecisionLedger.scenarioCount,
            ledgerHash: generatedScenarioDecisionLedger.ledgerHash,
            topDecision: {
              scenarioId: generatedScenarioDecisionLedger.topDecision.scenarioId,
              decisionLane: generatedScenarioDecisionLedger.topDecision.decisionLane,
              decisionReadinessScore:
                generatedScenarioDecisionLedger.topDecision.decisionReadinessScore,
              decisionHash: generatedScenarioDecisionLedger.topDecision.decisionHash,
            },
            selectedDecision:
              generatedScenarioDecisionLedger.decisions.find(
                (decision) => decision.scenarioId === selectedScenario.id
              ) ?? null,
          }
        : null,
      scenarioDecisionAuditLedger: generatedScenarioDecisionAuditLedger
        ? {
            mode: generatedScenarioDecisionAuditLedger.mode,
            auditCount: generatedScenarioDecisionAuditLedger.auditCount,
            commercialUseAllowed:
              generatedScenarioDecisionAuditLedger.commercialUseAllowed,
            ledgerHash: generatedScenarioDecisionAuditLedger.ledgerHash,
            leaderScenarioId: generatedScenarioDecisionAuditLedger.leaderScenarioId,
            topCrossoverQuestions:
              generatedScenarioDecisionAuditLedger.topCrossoverQuestions,
          }
        : null,
      scenarioCrossoverFrontier: generatedScenarioCrossoverFrontierLedger
        ? {
            mode: generatedScenarioCrossoverFrontierLedger.mode,
            commercialUseAllowed:
              generatedScenarioCrossoverFrontierLedger.commercialUseAllowed,
            leaderScenarioId: generatedScenarioCrossoverFrontierLedger.leaderScenarioId,
            challengerCount: generatedScenarioCrossoverFrontierLedger.challengerCount,
            nearestCrossover: generatedScenarioCrossoverFrontierLedger.nearestCrossover
              ? {
                  challengerScenarioId:
                    generatedScenarioCrossoverFrontierLedger.nearestCrossover
                      .challengerScenarioId,
                  requiredUtilityDelta:
                    generatedScenarioCrossoverFrontierLedger.nearestCrossover.requiredUtilityDelta,
                  crossoverState:
                    generatedScenarioCrossoverFrontierLedger.nearestCrossover.crossoverState,
                  crossoverHash:
                    generatedScenarioCrossoverFrontierLedger.nearestCrossover.crossoverHash,
                }
              : null,
            ledgerHash: generatedScenarioCrossoverFrontierLedger.ledgerHash,
          }
        : null,
      scenarioUncertaintyBudget: generatedScenarioUncertaintyBudgetLedger
        ? {
            mode: generatedScenarioUncertaintyBudgetLedger.mode,
            scenarioCount: generatedScenarioUncertaintyBudgetLedger.scenarioCount,
            iterations: generatedScenarioUncertaintyBudgetLedger.iterations,
            seed: generatedScenarioUncertaintyBudgetLedger.seed,
            commercialUseAllowed:
              generatedScenarioUncertaintyBudgetLedger.commercialUseAllowed,
            ledgerHash: generatedScenarioUncertaintyBudgetLedger.ledgerHash,
            highestUncertaintyScenario: {
              scenarioId:
                generatedScenarioUncertaintyBudgetLedger.highestUncertaintyScenario
                  .scenarioId,
              uncertaintyBudgetPoints:
                generatedScenarioUncertaintyBudgetLedger.highestUncertaintyScenario
                  .uncertaintyBudgetPoints,
              uncertaintyClass:
                generatedScenarioUncertaintyBudgetLedger.highestUncertaintyScenario
                  .uncertaintyClass,
            },
            selectedBudget: selectedUncertaintyBudget
              ? {
                  scenarioId: selectedUncertaintyBudget.scenarioId,
                  uncertaintyBudgetPoints:
                    selectedUncertaintyBudget.uncertaintyBudgetPoints,
                  uncertaintyClass: selectedUncertaintyBudget.uncertaintyClass,
                  topUncertaintyDrivers:
                    selectedUncertaintyBudget.topUncertaintyDrivers,
                  budgetHash: selectedUncertaintyBudget.budgetHash,
                }
              : null,
          }
        : null,
      capabilityMaturityLedger: generatedCapabilityMaturityLedger
        ? {
            mode: generatedCapabilityMaturityLedger.mode,
            capabilityCount: generatedCapabilityMaturityLedger.capabilityCount,
            blockedCapabilityCount:
              generatedCapabilityMaturityLedger.blockedCapabilityCount,
            commercializationAllowed:
              generatedCapabilityMaturityLedger.commercializationAllowed,
            ledgerHash: generatedCapabilityMaturityLedger.ledgerHash,
            topCapabilities: generatedCapabilityMaturityLedger.topCapabilities,
            globalBlockers: generatedCapabilityMaturityLedger.globalBlockers,
          }
        : null,
      modelValidationProtocols: generatedModelValidationProtocolLedger
        ? {
            mode: generatedModelValidationProtocolLedger.mode,
            protocolCount: generatedModelValidationProtocolLedger.protocolCount,
            blockedProtocolCount:
              generatedModelValidationProtocolLedger.blockedProtocolCount,
            commercialUseAllowed:
              generatedModelValidationProtocolLedger.commercialUseAllowed,
            ledgerHash: generatedModelValidationProtocolLedger.ledgerHash,
            stateCounts: generatedModelValidationProtocolLedger.stateCounts,
            topProtocolGaps:
              generatedModelValidationProtocolLedger.topProtocolGaps.slice(0, 5),
          }
        : null,
      workspaces: generatedWorkspaceLedger
        ? {
            mode: generatedWorkspaceLedger.mode,
            workspaceCount: generatedWorkspaceLedger.workspaceCount,
            blockedWorkspaceCount: generatedWorkspaceLedger.blockedWorkspaceCount,
            ledgerHash: generatedWorkspaceLedger.ledgerHash,
            selectedWorkspaces: generatedWorkspaceLedger.workspaces
              .filter((workspace) =>
                workspace.selectedScenarios.includes(selectedScenario.id)
              )
              .map((workspace) => ({
                id: workspace.id,
                title: workspace.title,
                reviewStage: workspace.reviewStage,
                workspaceHash: workspace.workspaceHash,
                blockedGateCount: workspace.reviewGates.filter(
                  (gate) => gate.status === "blocked"
                ).length,
              })),
          }
        : null,
      sidecarValidationReportHash: generatedSidecarValidationReport?.reportHash ?? null,
      sidecarScaffoldRejectionLedgerHash:
        generatedSidecarScaffoldRejectionLedger?.ledgerHash ?? null,
      sidecarWorkOrderPacketHash: generatedSidecarWorkOrderPacket?.packetHash ?? null,
      sourceIngestionLedgerHash: generatedSourceIngestionLedger?.ledgerHash ?? null,
      sourceUseReviewPacketHash: generatedSourceUseReviewPacket?.packetHash ?? null,
      sourceUseRightsLedgerHash: generatedSourceUseRightsLedger?.ledgerHash ?? null,
      sourceUseClearanceDossierHash: generatedSourceUseClearanceDossier?.dossierHash ?? null,
      sourceIngestionLedger: generatedSourceIngestionLedger
        ? {
            mode: generatedSourceIngestionLedger.mode,
            sourceCount: generatedSourceIngestionLedger.sourceCount,
            workOrderCount: generatedSourceIngestionLedger.workOrderCount,
            blockedWorkOrderCount: generatedSourceIngestionLedger.blockedWorkOrderCount,
            ledgerHash: generatedSourceIngestionLedger.ledgerHash,
            topPrioritySources: generatedSourceIngestionLedger.priorityQueue.slice(0, 5),
            globalBlockers: generatedSourceIngestionLedger.globalBlockers,
          }
        : null,
      sourceUseRightsLedger: generatedSourceUseRightsLedger
        ? {
            mode: generatedSourceUseRightsLedger.mode,
            sourceCount: generatedSourceUseRightsLedger.sourceCount,
            blockedSourceUseCount:
              generatedSourceUseRightsLedger.blockedSourceUseCount,
            customerUseAllowed: generatedSourceUseRightsLedger.customerUseAllowed,
            rawRedistributionAllowed:
              generatedSourceUseRightsLedger.rawRedistributionAllowed,
            ledgerHash: generatedSourceUseRightsLedger.ledgerHash,
            classCounts: generatedSourceUseRightsLedger.classCounts,
            riskCounts: generatedSourceUseRightsLedger.riskCounts,
            topReviewQueue:
              generatedSourceUseRightsLedger.topReviewQueue.slice(0, 8),
          }
        : null,
      sourceUseReviewPackets: generatedSourceUseReviewPacket
        ? {
            mode: generatedSourceUseReviewPacket.mode,
            packetCount: generatedSourceUseReviewPacket.packetCount,
            blockedPacketCount: generatedSourceUseReviewPacket.blockedPacketCount,
            customerUseAllowed: generatedSourceUseReviewPacket.customerUseAllowed,
            rawRedistributionAllowed:
              generatedSourceUseReviewPacket.rawRedistributionAllowed,
            packetHash: generatedSourceUseReviewPacket.packetHash,
            priorityCounts: generatedSourceUseReviewPacket.priorityCounts,
            topReviewQueue: generatedSourceUseReviewPacket.reviewQueue.slice(0, 8),
          }
        : null,
      sourceUseClearanceDossier: generatedSourceUseClearanceDossier
        ? {
            mode: generatedSourceUseClearanceDossier.mode,
            sourceCount: generatedSourceUseClearanceDossier.sourceCount,
            blockedSourceCount: generatedSourceUseClearanceDossier.blockedSourceCount,
            classCount: generatedSourceUseClearanceDossier.classCount,
            customerUseAllowed: generatedSourceUseClearanceDossier.customerUseAllowed,
            rawRedistributionAllowed: generatedSourceUseClearanceDossier.rawRedistributionAllowed,
            dossierHash: generatedSourceUseClearanceDossier.dossierHash,
            releaseImpact: generatedSourceUseClearanceDossier.releaseImpact,
            classDossiers: generatedSourceUseClearanceDossier.classDossiers.map((record) => ({
              sourceUseClass: record.sourceUseClass,
              priority: record.priority,
              sourceCount: record.sourceCount,
              decisionState: record.decisionState,
              customerUseAllowed: record.customerUseAllowed,
              blockedUntil: record.blockedUntil,
              classDossierHash: record.classDossierHash,
            })),
          }
        : null,
      sidecarWorkOrders: generatedSidecarWorkOrderPacket
        ? {
            mode: generatedSidecarWorkOrderPacket.mode,
            workOrderCount: generatedSidecarWorkOrderPacket.workOrderCount,
            blockedWorkOrderCount: generatedSidecarWorkOrderPacket.blockedWorkOrderCount,
            packetHash: generatedSidecarWorkOrderPacket.packetHash,
            firstRunnableContract: generatedSidecarWorkOrderPacket.workOrders[0]?.contractId ?? null,
          }
        : null,
      sidecarValidation: generatedSidecarValidationReport
        ? {
            mode: generatedSidecarValidationReport.mode,
            contractCount: generatedSidecarValidationReport.contractCount,
            validationCount: generatedSidecarValidationReport.validationCount,
            promotedCount: generatedSidecarValidationReport.promotedCount,
            blockedCount: generatedSidecarValidationReport.blockedCount,
            averageFieldCoveragePercent:
              generatedSidecarValidationReport.averageFieldCoveragePercent,
            promotionRule: generatedSidecarValidationReport.promotionRule,
          }
        : null,
      sidecarScaffoldRejections: generatedSidecarScaffoldRejectionLedger
        ? {
            mode: generatedSidecarScaffoldRejectionLedger.mode,
            scaffoldCount: generatedSidecarScaffoldRejectionLedger.scaffoldCount,
            rejectedScaffoldCount:
              generatedSidecarScaffoldRejectionLedger.rejectedScaffoldCount,
            unexpectedPromotionCount:
              generatedSidecarScaffoldRejectionLedger.unexpectedPromotionCount,
            scaffoldGatePassed:
              generatedSidecarScaffoldRejectionLedger.scaffoldGatePassed,
            ledgerHash: generatedSidecarScaffoldRejectionLedger.ledgerHash,
            rejectedContractIds:
              generatedSidecarScaffoldRejectionLedger.rejectedContractIds,
            evidenceBoundary:
              generatedSidecarScaffoldRejectionLedger.evidenceBoundary,
          }
        : null,
      generatedArtifactCount: generatedModelArtifactBundle.artifactCount,
      blockedArtifactCount: generatedModelArtifactBundle.blockedArtifactCount,
      claimEvidenceLedger: generatedClaimEvidenceLedger
        ? {
            mode: generatedClaimEvidenceLedger.mode,
            claimCount: generatedClaimEvidenceLedger.claimCount,
            verifiedClaimCount: generatedClaimEvidenceLedger.verifiedClaimCount,
            blockedClaimCount: generatedClaimEvidenceLedger.blockedClaimCount,
            ledgerHash: generatedClaimEvidenceLedger.ledgerHash,
            gradeCounts: generatedClaimEvidenceLedger.gradeCounts,
          }
        : null,
      verifiedClaims: verifiedClaims.map((claim) => ({
        id: claim.id,
        claim: claim.claim,
        confidence: claim.confidence,
        sources: sourceSummaries(claim.sourceIds, sourceRegistryById),
      })),
      highImpactUnverifiedClaims: highImpactUnverified.map((claim) => ({
        id: claim.id,
        claim: claim.claim,
        status: claim.status,
        confidence: claim.confidence,
        blocker: "Cannot be used as a commercialization proof until promoted.",
      })),
      modelArtifacts: modelArtifacts.map((artifact) => ({
        id: artifact.id,
        name: artifact.name,
        claimState: artifact.claimState,
        runtimeStatus: artifact.runtimeStatus,
        artifactHash: artifact.artifactHash,
        nextAction: artifact.nextAction,
      })),
    },
    assumptions: assumptionRegistry.map((assumption) => ({
      id: assumption.id,
      title: assumption.title,
      state: assumption.state,
      uncertainty: assumption.uncertainty,
      testGate: assumption.testGate,
      impact:
        generatedAssumptionImpactLedger?.assumptionImpacts.find(
          (impact) => impact.assumptionId === assumption.id
        ) ?? null,
    })),
    notebook: selectedNotebook
      ? {
          id: selectedNotebook.id,
          title: selectedNotebook.title,
          openQuestions: selectedNotebook.openQuestions,
          sourceIds: selectedNotebook.sourceIds,
          artifactIds: selectedNotebook.artifactIds,
        }
      : null,
    readiness: {
      readyForPublicBeta: blockingReadiness.length === 0,
      blockers: blockingReadiness.map((category) => ({
        id: category.id,
        label: category.label,
        currentScore: category.currentScore,
        targetScore: category.targetScore,
        blockers: category.currentBlockers,
      })),
    },
    governance: {
      socialSignalRule:
        "Forum and social-platform records are market signals only and cannot verify science claims without a primary-source bridge.",
      economicsRule:
        "Business-case cost outputs are planning heuristics until dated cost-source records are attached.",
      sourceUseRule:
        "Source records are provenance only; legal/customer use requires terms capture, citation policy, redistribution review, and reviewer approval.",
      promotionRule: generatedModelArtifactBundle.promotionRule,
    },
  };

  return {
    ...reportCore,
    reportHash: fnv1a32RecordHash(reportCore),
  };
}
